import { useAnalytics } from "$global-providers/analytics-provider/provider";
import { differenceInMilliseconds } from "date-fns";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useInterval } from "usehooks-ts";

export enum NetworkStatus {
  ONLINE = "ONLINE",
  OFFLINE = "OFFLINE",
}

const CHECK_INTERVAL_MS = 1000 * 1; // 1 second
const STATUS_UPDATE_DELAY_MS = 1000 * 5; // 5 seconds
const MIN_DOWNLINK_SPEED_MBPS = 0.05; // 50 kbps

// We can extend this function with further if statements to model other network states (eg unstable)
// or refactor it to read the network state from somewhere else later
const getNetworkStatus = (): NetworkStatus => {
  const downlinkSpeed = navigator?.connection?.downlink;
  if (
    (downlinkSpeed !== undefined && downlinkSpeed <= MIN_DOWNLINK_SPEED_MBPS) ||
    !navigator.onLine
  ) {
    return NetworkStatus.OFFLINE;
  }
  return NetworkStatus.ONLINE;
};

export const NetworkStatusContext = createContext<{
  networkStatus: NetworkStatus;
  isOfflineOnFirstLoad: boolean;
}>(null);

export const NetworkStatusProvider: FCwC<{
  checkIntervalMs?: number;
  statusUpdateDelayMs?: number;
}> = ({
  children,
  checkIntervalMs = CHECK_INTERVAL_MS,
  statusUpdateDelayMs = STATUS_UPDATE_DELAY_MS,
}) => {
  const hasAlreadyMounted = useRef(false);

  const { trackEvent } = useAnalytics();
  // Only once on first render.
  useEffect(() => {
    trackEvent("networkStatusChange", { networkStatus: getNetworkStatus() });
  });
  const [lastNetworkState, setLastNetworkState] = useState<{
    status: NetworkStatus;
    at: Date;
  }>({
    status: getNetworkStatus(),
    at: new Date(),
  });
  const [isOfflineOnFirstLoad, setIsOfflineOnFirstLoad] = useState(false);
  useEffect(() => {
    if (hasAlreadyMounted.current) {
      return;
    }
    hasAlreadyMounted.current = true;

    if (lastNetworkState.status === NetworkStatus.OFFLINE) {
      setIsOfflineOnFirstLoad(true);
    }
  }, [lastNetworkState.status]);

  useInterval(() => {
    const currentStatus = getNetworkStatus();
    if (
      currentStatus !== lastNetworkState.status &&
      differenceInMilliseconds(new Date(), lastNetworkState.at) >
        statusUpdateDelayMs
    ) {
      trackEvent("networkStatusChange", { networkStatus: currentStatus });
      setLastNetworkState({ status: currentStatus, at: new Date() });
    }
  }, checkIntervalMs);

  return (
    <NetworkStatusContext.Provider
      value={{
        networkStatus: lastNetworkState.status,
        isOfflineOnFirstLoad,
      }}
    >
      {children}
    </NetworkStatusContext.Provider>
  );
};

export const useNetworkStatus = () => {
  const context = useContext(NetworkStatusContext);
  if (!context) {
    throw new Error(
      "useNetworkStatus must be used within a NetworkStatusProvider",
    );
  }
  return context;
};
